import { useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { it, enUS, es } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalSelect, BrutalSelectContent, BrutalSelectItem, BrutalSelectTrigger, BrutalSelectValue } from "@/components/ui/BrutalSelect";
import { DemographicsMultiSelect } from "./DemographicsMultiSelect";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export interface DemographicsData {
  date_of_birth: Date | null;
  gender_identity: string | null;
  sexual_orientation: string | null;
  neurodivergences: string[];
  ethnicity: string | null;
  work_status: string | null;
  disabilities: string[];
}

interface DemographicsFormProps {
  initialData?: Partial<DemographicsData>;
  onSave: (data: DemographicsData) => Promise<void>;
  onSkip?: () => void;
  showSkipButton?: boolean;
  submitLabel?: string;
  isLoading?: boolean;
}

export const DemographicsForm = ({
  initialData,
  onSave,
  onSkip,
  showSkipButton = true,
  submitLabel,
  isLoading = false,
}: DemographicsFormProps) => {
  const { t, i18n } = useTranslation();
  
  const [dateOfBirth, setDateOfBirth] = useState<Date | undefined>(
    initialData?.date_of_birth ? new Date(initialData.date_of_birth) : undefined
  );
  const [genderIdentity, setGenderIdentity] = useState(initialData?.gender_identity || "");
  const [sexualOrientation, setSexualOrientation] = useState(initialData?.sexual_orientation || "");
  const [neurodivergences, setNeurodivergences] = useState<string[]>(initialData?.neurodivergences || []);
  const [ethnicity, setEthnicity] = useState(initialData?.ethnicity || "");
  const [workStatus, setWorkStatus] = useState(initialData?.work_status || "");
  const [disabilities, setDisabilities] = useState<string[]>(initialData?.disabilities || []);
  const [saving, setSaving] = useState(false);
  
  const getDateLocale = () => {
    switch (i18n.language?.substring(0, 2)) {
      case "it": return it;
      case "es": return es;
      default: return enUS;
    }
  };

  const genderOptions = [
    { value: "woman", label: t("onboarding.options.gender.woman") },
    { value: "man", label: t("onboarding.options.gender.man") },
    { value: "non-binary", label: t("onboarding.options.gender.nonBinary") },
    { value: "other", label: t("onboarding.options.gender.other") },
    { value: "prefer-not-to-say", label: t("onboarding.options.preferNotToSay") },
  ];

  const orientationOptions = [
    { value: "heterosexual", label: t("onboarding.options.orientation.heterosexual") },
    { value: "gay", label: t("onboarding.options.orientation.gay") },
    { value: "lesbian", label: t("onboarding.options.orientation.lesbian") },
    { value: "bisexual", label: t("onboarding.options.orientation.bisexual") },
    { value: "pansexual", label: t("onboarding.options.orientation.pansexual") },
    { value: "asexual", label: t("onboarding.options.orientation.asexual") },
    { value: "queer", label: t("onboarding.options.orientation.queer") },
    { value: "other", label: t("onboarding.options.orientation.other") },
    { value: "prefer-not-to-say", label: t("onboarding.options.preferNotToSay") },
  ];

  const neurodivergenceOptions = [
    { value: "adhd", label: t("onboarding.options.neurodivergences.adhd") },
    { value: "autism", label: t("onboarding.options.neurodivergences.autism") },
    { value: "dyslexia", label: t("onboarding.options.neurodivergences.dyslexia") },
    { value: "other", label: t("onboarding.options.neurodivergences.other") },
    { value: "none", label: t("onboarding.options.neurodivergences.none") },
    { value: "prefer-not-to-say", label: t("onboarding.options.preferNotToSay") },
  ];

  const ethnicityOptions = [
    { value: "caucasian-european", label: t("onboarding.options.ethnicity.caucasian") },
    { value: "african", label: t("onboarding.options.ethnicity.african") },
    { value: "asian", label: t("onboarding.options.ethnicity.asian") },
    { value: "latin-american", label: t("onboarding.options.ethnicity.latinAmerican") },
    { value: "middle-eastern", label: t("onboarding.options.ethnicity.middleEastern") },
    { value: "mixed", label: t("onboarding.options.ethnicity.mixed") },
    { value: "other", label: t("onboarding.options.ethnicity.other") },
    { value: "prefer-not-to-say", label: t("onboarding.options.preferNotToSay") },
  ];

  const workStatusOptions = [
    { value: "employee", label: t("onboarding.options.workStatus.employee") },
    { value: "freelance", label: t("onboarding.options.workStatus.freelance") },
    { value: "entrepreneur", label: t("onboarding.options.workStatus.entrepreneur") },
    { value: "student", label: t("onboarding.options.workStatus.student") },
    { value: "job-seeker", label: t("onboarding.options.workStatus.jobSeeker") },
    { value: "other", label: t("onboarding.options.workStatus.other") },
    { value: "prefer-not-to-say", label: t("onboarding.options.preferNotToSay") },
  ];

  const disabilityOptions = [
    { value: "motor", label: t("onboarding.options.disabilities.motor") },
    { value: "visual", label: t("onboarding.options.disabilities.visual") },
    { value: "hearing", label: t("onboarding.options.disabilities.hearing") },
    { value: "invisible-chronic", label: t("onboarding.options.disabilities.invisibleChronic") },
    { value: "none", label: t("onboarding.options.disabilities.none") },
    { value: "prefer-not-to-say", label: t("onboarding.options.preferNotToSay") },
  ];

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSave({
        date_of_birth: dateOfBirth || null,
        gender_identity: genderIdentity || null,
        sexual_orientation: sexualOrientation || null,
        neurodivergences,
        ethnicity: ethnicity || null,
        work_status: workStatus || null,
        disabilities,
      });
    } finally {
      setSaving(false);
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="space-y-6">
      {/* Date of Birth */}
      <div className="space-y-2">
        <Label>
          {t("onboarding.fields.dateOfBirth")}
        </Label>
        <Popover>
          <PopoverTrigger asChild>
            <div
              className={cn(
                "w-full border-[3px] border-dark bg-card px-3 py-3 font-body text-base",
                "flex items-center gap-2 cursor-pointer transition-all",
                "hover:shadow-[4px_4px_0px_0px] hover:shadow-dark hover:translate-x-[-2px] hover:translate-y-[-2px]",
                !dateOfBirth && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="h-5 w-5 shrink-0" />
              <span className="flex-1">
                {dateOfBirth
                  ? format(dateOfBirth, "dd/MM/yyyy", { locale: getDateLocale() })
                  : t("onboarding.fields.selectDate")}
              </span>
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={dateOfBirth}
              onSelect={setDateOfBirth}
              disabled={(date) => date > new Date() || date < new Date("1920-01-01")}
              captionLayout="dropdown"
              fromYear={1920}
              toYear={currentYear}
              initialFocus
              locale={getDateLocale()}
              className="p-3 pointer-events-auto"
            />
          </PopoverContent>
        </Popover>
        <p className="text-xs text-muted-foreground">
          {t("onboarding.fields.dateOfBirthHint")}
        </p>
      </div>

      {/* Gender Identity */}
      <div className="space-y-2">
        <Label>
          {t("onboarding.fields.genderIdentity")}
        </Label>
        <BrutalSelect value={genderIdentity} onValueChange={setGenderIdentity}>
          <BrutalSelectTrigger>
            <BrutalSelectValue placeholder={t("onboarding.fields.selectOption")} />
          </BrutalSelectTrigger>
          <BrutalSelectContent>
            {genderOptions.map((option) => (
              <BrutalSelectItem key={option.value} value={option.value}>
                {option.label}
              </BrutalSelectItem>
            ))}
          </BrutalSelectContent>
        </BrutalSelect>
      </div>

      {/* Sexual Orientation */}
      <div className="space-y-2">
        <Label>
          {t("onboarding.fields.sexualOrientation")}
        </Label>
        <BrutalSelect value={sexualOrientation} onValueChange={setSexualOrientation}>
          <BrutalSelectTrigger>
            <BrutalSelectValue placeholder={t("onboarding.fields.selectOption")} />
          </BrutalSelectTrigger>
          <BrutalSelectContent>
            {orientationOptions.map((option) => (
              <BrutalSelectItem key={option.value} value={option.value}>
                {option.label}
              </BrutalSelectItem>
            ))}
          </BrutalSelectContent>
        </BrutalSelect>
      </div>

      {/* Neurodivergences */}
      <div className="space-y-2">
        <Label>
          {t("onboarding.fields.neurodivergences")}
        </Label>
        <DemographicsMultiSelect
          options={neurodivergenceOptions}
          selected={neurodivergences}
          onChange={setNeurodivergences}
          placeholder={t("onboarding.fields.selectMultiple")}
        />
      </div>

      {/* Ethnicity */}
      <div className="space-y-2">
        <Label>
          {t("onboarding.fields.ethnicity")}
        </Label>
        <BrutalSelect value={ethnicity} onValueChange={setEthnicity}>
          <BrutalSelectTrigger>
            <BrutalSelectValue placeholder={t("onboarding.fields.selectOption")} />
          </BrutalSelectTrigger>
          <BrutalSelectContent>
            {ethnicityOptions.map((option) => (
              <BrutalSelectItem key={option.value} value={option.value}>
                {option.label}
              </BrutalSelectItem>
            ))}
          </BrutalSelectContent>
        </BrutalSelect>
      </div>

      {/* Work Status */}
      <div className="space-y-2">
        <Label>
          {t("onboarding.fields.workStatus")}
        </Label>
        <BrutalSelect value={workStatus} onValueChange={setWorkStatus}>
          <BrutalSelectTrigger>
            <BrutalSelectValue placeholder={t("onboarding.fields.selectOption")} />
          </BrutalSelectTrigger>
          <BrutalSelectContent>
            {workStatusOptions.map((option) => (
              <BrutalSelectItem key={option.value} value={option.value}>
                {option.label}
              </BrutalSelectItem>
            ))}
          </BrutalSelectContent>
        </BrutalSelect>
      </div>

      {/* Disabilities */}
      <div className="space-y-2">
        <Label>
          {t("onboarding.fields.disabilities")}
        </Label>
        <DemographicsMultiSelect
          options={disabilityOptions}
          selected={disabilities}
          onChange={setDisabilities}
          placeholder={t("onboarding.fields.selectMultiple")}
        />
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3 pt-4">
        <BrutalButton
          onClick={handleSubmit}
          disabled={saving || isLoading}
          className="w-full"
        >
          {saving ? t("common.saving") : (submitLabel || t("onboarding.save"))}
        </BrutalButton>
        
        {showSkipButton && onSkip && (
          <BrutalButton
            variant="outline"
            onClick={onSkip}
            disabled={saving || isLoading}
            className="w-full"
          >
            {t("onboarding.skip")}
          </BrutalButton>
        )}
      </div>
    </div>
  );
};
